import API from "apiHelpers";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserAction } from 'store/actions';
import { WALLET_STATUS } from "store/user/slice";
import { UTILS } from "utils";
import { logErrorOnSentry } from "utils/Utils";

// Shared across every component that mounts the hook, so that components
// mounting together issue one request instead of one each. Cleared once the
// request settles, so a later mount still refetches.
let inFlightRequest = null;
let inFlightKey = null;

function requestUserWallet(key, params) {
  if (inFlightRequest && inFlightKey === key) {
    return inFlightRequest;
  }

  inFlightKey = key;
  inFlightRequest = API.getUserWallet(params).finally(() => {
    if (inFlightKey === key) {
      inFlightRequest = null;
      inFlightKey = null;
    }
  });

  return inFlightRequest;
}

/**
 * Loyalty wallet balance, with its load state.
 *
 * `balance` is null while the balance is unknown, so callers can tell "not
 * loaded yet" apart from "no balance". Never coerce it with `|| 0` before
 * checking `isLoading`: that is what used to make the loyalty control vanish
 * and the redeem amount reset to zero on every mount.
 */
function useUserWalletState() {
  const dispatch = useDispatch();
  const { brandInfo } = useSelector((state) => state?.brandStorageReducer);
  const { walletBalance, walletBrandId, walletStatus } = useSelector(
    (state) => state?.user
  );

  const brandId = brandInfo?.brand?._id;

  useEffect(() => {
    const currentUser = UTILS.getCurrentUser(brandId);

    if (
      !currentUser?.token ||
      !currentUser?.user?.userId ||
      !currentUser?.user?.brandId
    ) {
      // No signed-in user: the balance is known to be nothing, not unknown.
      dispatch(UserAction.setWalletBalance({ balance: 0, brandId: brandId ?? null }));
      return;
    }

    const userId = currentUser.user.userId;
    const walletBrand = currentUser.user.brandId;
    const key = `${walletBrand}:${userId}`;

    dispatch(UserAction.setWalletLoading({ brandId: walletBrand }));

    requestUserWallet(key, { userId, brandId: walletBrand })
      .then((userWallet) => {
        if (userWallet?.success === 1) {
          dispatch(
            UserAction.setWalletBalance({
              balance: userWallet?.data?.balance || 0,
              brandId: walletBrand,
            })
          );
          dispatch(
            UserAction.setLifetimeSpending(userWallet?.data?.lifetimeSpending || 0)
          );
        } else {
          dispatch(UserAction.setWalletError({ brandId: walletBrand }));
        }
      })
      .catch((err) => {
        dispatch(UserAction.setWalletError({ brandId: walletBrand }));
        logErrorOnSentry(
          `user wallet api error ${err?.message}`,
          "error",
          `user-info:${JSON.stringify(currentUser)}`
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // A balance stored against a different brand is not an answer for this one.
  const isSameBrand = walletBrandId === (brandId ?? null);
  const balance =
    isSameBrand && walletBalance !== null ? Number(walletBalance) : null;
  // A failed refresh on top of a balance we already have is not a failure to
  // report: the last known balance is still the best answer available.
  const hasError =
    isSameBrand && walletStatus === WALLET_STATUS.ERROR && balance === null;

  return {
    balance,
    // Unknown counts as loading: idle means the fetch has not reported yet.
    isLoading: balance === null && !hasError,
    isLoaded: balance !== null,
    hasError,
  };
}

/**
 * Backwards-compatible number form for callers that only display the balance.
 * Prefer `useUserWalletState` anywhere a zero changes behaviour.
 */
function useUserWallet() {
  const { balance } = useUserWalletState();
  return balance ?? 0;
}

export { useUserWalletState };
export default useUserWallet;
